import deepmerge from 'deepmerge';
import { useBusEmitter } from '~/composables/useBus';
import { type ProviderConfig } from '~/types/system';

export default function useCollection () {
  const { query } = useTansQuery();
  let additionalOptions = {};
  const apiRef = ref('');
  const paramOptions = ref({});
  const FILTER_OPTIONS = ['enabled', 'removeUsed'];
  const getData = (provider: string, options: Record<any, any>) => {
    const providerConfig = useRuntimeConfig()?.public?.[provider] as ProviderConfig;
    apiRef.value = (import.meta.client && providerConfig?.api ? providerConfig?.api : generateServerUrl(useRuntimeConfig()?.[provider]?.originApi)) as string;
    if (!apiRef.value) {
      return {
        response: ref({}),
        loading: ref(false)
      };
    }
    paramOptions.value = options;
    const { skipIds, ...restOptions } = options;

    const { data, loading } = query({ api: createApi, ...restOptions });
    return {
      response: data,
      loading
    };
  };
  const getFirebaseData = (provider: string, options: Record<any, any>) => {
    const { initFirebase, state } = useFirebase();
    const firebaseConfig = options?.options?.value.firebaseConfig;
    const providerPath = useRuntimeConfig()?.public?.[provider]?.path || '';
    const clientId = firebaseConfig?.data?.clientId;
    const editionId = options?.options?.value.id;
    const userId = firebaseConfig?.data?.userId;
    const itemState = options?.options?.value.state || '';
    onMounted(async () => {
      await initFirebase(firebaseConfig.config, firebaseConfig.token, providerPath, clientId, editionId, userId, itemState);
    });

    if (!state) {
      console.warn('Preview data state not found in context.');
      return {
        response: ref({}),
        loading: ref(false)
      };
    }
    const response = ref(state.value.response);

    const loading = ref(state.value.loading);

    watch(
      () => state,
      (newValue) => {
        if (newValue) {
          response.value = state.value.response;
          loading.value = state.value.loading;
        }
      },
      { immediate: true, deep: true }
    );

    return {
      response,
      loading
    };
  };
  const createApi = computed(() => {
    const { replaceUrl } = useUrlReplace();
    const filteredOptions = Object.keys(paramOptions.value.options).reduce((obj, key) => {
      if (!FILTER_OPTIONS.includes(key) && paramOptions.value.options[key] != null) {
        obj[key] = paramOptions.value.options[key];
      }
      return obj;
    }, {} as Record<string, any>);
    const uniqueSkipIds = [...new Set(paramOptions.value?.skipIds)];
    const skipIdsParam = uniqueSkipIds.length > 0 ? { skipIds: uniqueSkipIds.join(',') } : {};
    return replaceUrl(apiRef.value, { ...filteredOptions, ...skipIdsParam });
  });

  const getOptions = (payload: Record<string, any>, defaultOptions: Record<string, any>) => {
    additionalOptions = deepmerge(defaultOptions || {}, (payload && payload.options) || {});
    useBusEmitter('additionalOptions', { options: additionalOptions });
    return additionalOptions;
  };

  return {
    getData,
    getFirebaseData,
    getOptions
  };
}
